import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { isSkiPeriod } from '@/lib/season';

type L = 'sr' | 'en' | 'ru';
const T = {
  title: { sr: 'Zimske tegobe — pomažemo odmah', en: 'Winter complaints — we can help now', ru: 'Зимние проблемы — поможем сейчас' },
  sub: {
    sr: 'Hladnoća, više sedenja i manje kretanja često donose ukočen vrat i bol u leđima. Pregled je besplatan, plaća se samo tretman.',
    en: 'Cold weather, more sitting and less movement often bring a stiff neck and back pain. The assessment is free — you pay only for treatment.',
    ru: 'Холод, больше сидения и меньше движения часто вызывают скованность шеи и боль в спине. Осмотр бесплатный — оплачивается только процедура.',
  },
  call: { sr: 'Pozovite: 060 503 7477', en: 'Call: +381 60 503 7477', ru: 'Звоните: +381 60 503 7477' },
};
const ITEMS: { to: string; promo?: boolean; title: Record<L, string>; text: Record<L, string> }[] = [
  {
    to: '/kiropraktika-novi-beograd', promo: true,
    title: { sr: 'Kiropraktika + fizikalna terapija', en: 'Chiropractic + physical therapy', ru: 'Хиропрактика + физиотерапия' },
    text: { sr: 'Akcija 5.500 RSD (redovno 7.500 RSD).', en: 'Promo 5,500 RSD (regular 7,500 RSD).', ru: 'Акция 5 500 RSD (обычно 7 500 RSD).' },
  },
  {
    to: '/blog/ukocen-vrat-tortikolis-sta-pomaze',
    title: { sr: 'Bol u vratu i leđima', en: 'Neck and back pain', ru: 'Боль в шее и спине' },
    text: { sr: 'Ukočen vrat, bol između lopatica, bol od sedenja.', en: 'Stiff neck, pain between shoulder blades, pain from sitting.', ru: 'Скованная шея, боль между лопатками, боль от сидения.' },
  },
  {
    to: '/blog/fizikalna-terapija-posle-operacije-kuka',
    title: { sr: 'Rehabilitacija posle operacije', en: 'Post-surgery rehabilitation', ru: 'Реабилитация после операции' },
    text: { sr: 'Kuk, koleno, rame — vežbe uz terapeuta.', en: 'Hip, knee, shoulder — guided exercise.', ru: 'Тазобедренный сустав, колено, плечо — упражнения с терапевтом.' },
  },
];
const SKI = {
  to: '/ski-sezona',
  title: { sr: 'Ski povrede i priprema', en: 'Ski injuries and preparation', ru: 'Горнолыжные травмы и подготовка' },
  text: { sr: 'Koleno, rame, leđa posle skijanja.', en: 'Knee, shoulder, back after skiing.', ru: 'Колено, плечо, спина после лыж.' },
};
const PROMO = { sr: 'PROMO', en: 'PROMO', ru: 'АКЦИЯ' };

const WinterFocus = () => {
  const { i18n } = useTranslation();
  const lang = ((i18n.language || 'sr').split('-')[0] as L);
  const l: L = ['sr', 'en', 'ru'].includes(lang) ? lang : 'sr';
  const items = isSkiPeriod() ? [...ITEMS, SKI] : ITEMS;

  return (
    <section className="py-16 cv-auto" aria-labelledby="winter-focus-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 id="winter-focus-title" className="text-3xl lg:text-4xl font-bold mb-4">{T.title[l]}</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">{T.sub[l]}</p>
        </div>
        <div className={`grid gap-6 sm:grid-cols-2 ${items.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {items.map((it) => (
            <Link key={it.to} to={it.to} className="group block">
              <Card className="glass-card h-full relative hover:border-primary/50 transition-colors">
                {'promo' in it && it.promo && (
                  <span className="absolute top-3 right-3 rounded-full bg-accent text-accent-foreground text-xs font-bold px-3 py-1 animate-pulse">
                    {PROMO[l]}
                  </span>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2 pr-16 group-hover:text-primary transition-colors">{it.title[l]}</h3>
                  <p className="text-muted-foreground mb-4">{it.text[l]}</p>
                  <ArrowRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button variant="hero" size="lg" asChild>
            <a href="tel:+381605037477"><Phone className="mr-2 h-5 w-5" />{T.call[l]}</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WinterFocus;
